import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check, Terminal, Users, Shield, AlertTriangle, ArrowRight, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function DocsAgentAdmin() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const commands = [
    {
      command: '/agents',
      description: 'List all agents with their current status and trust tier.',
      example: `/agents

// Response:
// 🤖 Active Agents:
// ┌──────────────────┬───────────┬────────────┬──────────┐
// │ Agent            │ Status    │ Trust Tier  │ Mode     │
// ├──────────────────┼───────────┼────────────┼──────────┤
// │ pricing_agent    │ ✅ Active  │ Supervised  │ auto     │
// │ deal_agent       │ ✅ Active  │ Trusted     │ auto     │
// │ inventory_agent  │ ⏸️ Paused  │ Probation   │ approve  │
// │ content_agent    │ 🚫 Off    │ Probation   │ disabled │
// └──────────────────┴───────────┴────────────┴──────────┘`,
    },
    {
      command: '/agentmode <agent> <mode>',
      description: 'Set the approval mode for an agent. Modes: auto, approve, notify, disabled.',
      example: `/agentmode pricing_agent auto

// Response:
// ✅ pricing_agent mode set to auto.
// Actions will be auto-approved or held based on trust tier.

/agentmode deal_agent approve

// Response:
// ✅ deal_agent mode set to approve.
// All actions will require manual approval.`,
    },
    {
      command: '/mintier <agent> <tier>',
      description: 'Set the minimum trust tier for an agent. Tiers: probation, supervised, trusted, autonomous.',
      example: `/mintier pricing_agent supervised

// Response:
// ✅ Minimum tier for pricing_agent set to Supervised.
// Agent will not drop below this tier.`,
    },
    {
      command: '/threshold <agent> <value>',
      description: 'Set the confidence threshold for an agent (0.0 to 1.0).',
      example: `/threshold pricing_agent 0.85

// Response:
// ✅ Confidence threshold for pricing_agent set to 0.85.
// Actions with confidence below 0.85 will be escalated.`,
    },
    {
      command: '/policy',
      description: 'View current policies for all agents.',
      example: `/policy

// Response:
// 📋 Current Agent Policies:
//
// pricing_agent:
//   approval_mode: auto
//   confidence_threshold: 0.85
//   max_actions_per_hour: 50
//   max_financial_impact: $1,000
//   trust_tier: supervised
//   min_tier: supervised
//
// deal_agent:
//   approval_mode: auto
//   confidence_threshold: 0.7
//   max_actions_per_hour: 30
//   max_financial_impact: $500
//   trust_tier: trusted
//   min_tier: probation`,
    },
    {
      command: '/policy set <key> <value>',
      description: 'Update a specific policy value.',
      example: `/policy set pricing_agent.max_actions_per_hour 25

// Response:
// ✅ pricing_agent.max_actions_per_hour set to 25.

/policy set deal_agent.max_financial_impact 750

// Response:
// ✅ deal_agent.max_financial_impact set to $750.`,
    },
  ];

  const merchantTiers = [
    {
      tier: 'Newcomer',
      requirements: 'New account',
      max_discount_pct: 10,
      daily_transaction_limit: '$500',
      agents_allowed: 1,
    },
    {
      tier: 'Starter',
      requirements: '7+ days, 10+ orders',
      max_discount_pct: 15,
      daily_transaction_limit: '$2,000',
      agents_allowed: 2,
    },
    {
      tier: 'Growth',
      requirements: '30+ days, 50+ orders',
      max_discount_pct: 25,
      daily_transaction_limit: '$10,000',
      agents_allowed: 4,
    },
    {
      tier: 'Established',
      requirements: '90+ days, 200+ orders',
      max_discount_pct: 35,
      daily_transaction_limit: '$50,000',
      agents_allowed: 6,
    },
    {
      tier: 'Legendary',
      requirements: '180+ days, 1000+ orders',
      max_discount_pct: 50,
      daily_transaction_limit: '$200,000',
      agents_allowed: 10,
    },
  ];

  const policyModelExample = `// MerchantAgentPolicy model
{
  "merchant_id": "merch_xyz789",
  "merchant_tier": "growth",        // newcomer/starter/growth/established/legendary
  "global_policies": {
    "max_discount_pct": 25,         // Maximum discount any agent can create
    "daily_transaction_limit": 10000,
    "max_agents": 4
  },
  "agent_policies": {
    "pricing_agent": {
      "approval_mode": "auto",
      "confidence_threshold": 0.85,
      "max_actions_per_hour": 50,
      "max_financial_impact": 1000,
      "trust_tier": "supervised",
      "min_tier": "supervised",
      "trust_tier_auto_upgrade": true
    },
    "deal_agent": {
      "approval_mode": "auto",
      "confidence_threshold": 0.7,
      "max_actions_per_hour": 30,
      "max_financial_impact": 500,
      "trust_tier": "trusted",
      "min_tier": "probation",
      "trust_tier_auto_upgrade": true
    }
  }
}`;

  const emergencyExample = `// Emergency disable - immediately stops all agent actions
/agentmode pricing_agent disabled

// Response:
// 🚫 pricing_agent has been DISABLED.
// All pending actions have been cancelled.
// No new actions will be taken until re-enabled.
//
// To re-enable: /agentmode pricing_agent auto

// You can also disable all agents at once:
/agentmode all disabled

// Response:
// 🚫 All agents have been DISABLED.
// 4 pending actions cancelled.
// Use /agentmode <agent> <mode> to re-enable individually.`;

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          AI Agents
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Agent Administration
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Manage your AI agents through messaging commands. Control approval modes, trust tiers,
          confidence thresholds, and policies directly from Telegram or WhatsApp.
        </p>
      </div>

      {/* Messaging Commands */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Terminal className="w-6 h-6 text-[#3EF1C7]" />
          Messaging Commands
        </h2>
        <p className="text-[#94A3B8] mb-6">
          All commands can be used in your connected messaging channel (Telegram or WhatsApp).
          The bot will respond with confirmation and current state.
        </p>
        <div className="space-y-6">
          {commands.map((cmd, i) => (
            <Card key={i} className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <CardTitle className="text-[#E2E8F0] text-lg flex items-center gap-2">
                  <code className="text-[#3EF1C7] bg-[#3EF1C7]/10 px-2 py-1 rounded text-sm">{cmd.command}</code>
                </CardTitle>
                <p className="text-[#94A3B8] text-sm">{cmd.description}</p>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
                    onClick={() => copyToClipboard(cmd.example, `cmd-${i}`)}
                  >
                    {copiedCode === `cmd-${i}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
                    <code>{cmd.example}</code>
                  </pre>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* MerchantAgentPolicy Model */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Settings className="w-6 h-6 text-[#3EF1C7]" />
          MerchantAgentPolicy Model
        </h2>
        <p className="text-[#94A3B8] mb-4">
          The MerchantAgentPolicy model stores all agent configuration for a merchant. It includes
          global policies derived from your merchant tier and per-agent overrides.
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(policyModelExample, 'policymodel')}
          >
            {copiedCode === 'policymodel' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{policyModelExample}</code>
          </pre>
        </div>
      </section>

      {/* Merchant Tier Policies */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-[#3EF1C7]" />
          Tier-Based Policies
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Your merchant tier determines the upper bounds for agent capabilities. These limits cannot
          be exceeded regardless of individual agent guardrail settings.
        </p>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Tier</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Requirements</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Max Discount</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Daily Limit</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Agents</th>
                </tr>
              </thead>
              <tbody>
                {merchantTiers.map((row, i) => (
                  <tr key={i} className="border-b border-[#22304A] last:border-0">
                    <td className="p-3 text-[#3EF1C7] font-medium">{row.tier}</td>
                    <td className="p-3 text-[#94A3B8]">{row.requirements}</td>
                    <td className="p-3 text-[#E2E8F0]">{row.max_discount_pct}%</td>
                    <td className="p-3 text-[#E2E8F0]">{row.daily_transaction_limit}</td>
                    <td className="p-3 text-[#E2E8F0]">{row.agents_allowed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Emergency Disable */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-[#EF4444]" />
          Emergency Disable
        </h2>
        <p className="text-[#94A3B8] mb-4">
          If an agent is behaving unexpectedly, you can immediately disable it. This cancels all
          pending actions and prevents any new actions until you re-enable the agent.
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(emergencyExample, 'emergency')}
          >
            {copiedCode === 'emergency' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{emergencyExample}</code>
          </pre>
        </div>
      </section>

      {/* What's Next */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">What's Next?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/docs/trust-ladder">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Trust Ladder <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Learn how agents progress through trust tiers.</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/guardrails">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Guardrails <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Configure detailed safety limits for each agent.</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
